// Equipment slots, AC compute, proficiency, and quick-attack helpers.
// Pure functions — no React, no Firestore.

import { INVENTORY_CATALOG } from './inventory';

// Find catalog metadata for an inventory item by name. We store the
// inventory entries lean (just name/qty/etc.); the structured fields like
// `weapon`, `armor`, `use` live on the catalog and are looked up by name.
function catalogFor(item) {
  if (!item || !item.name) return null;
  return INVENTORY_CATALOG.find((c) => c.category === item.category && c.name === item.name) || null;
}

export function abilityMod(score) {
  return Math.floor(((score ?? 10) - 10) / 2);
}

export function profBonusForLevel(level) {
  return Math.ceil((level || 1) / 4) + 1;
}

// Which slot does this item want to occupy when equipped *by default*?
//   armor (non-shield) → 'armor'
//   shield             → 'offHand'
//   weapon two-handed  → 'mainHand' (also clears offHand on equip)
//   weapon other       → 'mainHand' (caller may explicitly pick 'offHand')
//   anything else      → null (not slotable)
export function slotForItem(item) {
  const cat = catalogFor(item);
  if (!cat) return null;
  if (cat.category === 'armor') {
    if (cat.armor?.type === 'shield') return 'offHand';
    return 'armor';
  }
  if (cat.category === 'weapon') return 'mainHand';
  return null;
}

// All slots an item is *legal* in. Drives the EquipTab's "Change…" pickers
// — a non-two-handed weapon can land in either hand, etc.
export function validSlotsFor(item) {
  const cat = catalogFor(item);
  if (!cat) return [];
  if (cat.category === 'armor') {
    if (cat.armor?.type === 'shield') return ['offHand'];
    return ['armor'];
  }
  if (cat.category === 'weapon') {
    if (cat.weapon?.properties?.includes('two-handed')) return ['mainHand'];
    return ['mainHand', 'offHand'];
  }
  return [];
}

// Effective slot of an equipped item, honouring an optional override on
// the item record (`equippedSlot`). Falls back to the item's default slot.
export function effectiveSlotOf(item) {
  if (!item || !item.equipped) return null;
  if (item.equippedSlot) return item.equippedSlot;
  return slotForItem(item);
}

export function isTwoHanded(item) {
  const cat = catalogFor(item);
  return !!cat?.weapon?.properties?.includes('two-handed');
}

export function isShield(item) {
  const cat = catalogFor(item);
  return cat?.armor?.type === 'shield';
}

// Apply slot exclusivity when equipping `itemId`. Returns the next inventory
// array. Two-handed weapons clear off-hand. Shields can't co-exist with
// two-handed mainhand weapons (the shield wins; the 2h is unequipped).
//
// `targetSlot` is optional — pass it when the user explicitly chose a slot
// (e.g. equipping a Longsword in the off-hand). If absent, the item's
// default slot is used. If targetSlot isn't legal for this item, falls
// back to the default.
export function equipItem(inventory, itemId, targetSlot) {
  const target = inventory.find((i) => i.id === itemId);
  if (!target) return inventory;

  const valid = validSlotsFor(target);
  const defaultSlot = slotForItem(target);
  const slot = (targetSlot && valid.includes(targetSlot)) ? targetSlot : defaultSlot;

  if (!slot) {
    // Non-slotable but still toggle (e.g., a non-equippable item somehow has
    // the equipped flag set — just flip it for that item alone).
    return inventory.map((i) => i.id === itemId ? { ...i, equipped: !i.equipped } : i);
  }

  const wasEquipped       = !!target.equipped;
  const wasInSameSlot     = wasEquipped && (effectiveSlotOf(target) === slot);

  if (wasInSameSlot) {
    // Tapping "equip" again on the same slot → unequip.
    return inventory.map((i) => i.id === itemId ? { ...i, equipped: false, equippedSlot: null } : i);
  }

  // Equip target into `slot`, unequip conflicting slots.
  const targetIsTwoHanded = isTwoHanded(target);
  const targetIsShield    = isShield(target);

  return inventory.map((i) => {
    if (i.id === itemId) return { ...i, equipped: true, equippedSlot: slot };
    if (!i.equipped) return i;
    const otherSlot = effectiveSlotOf(i);
    if (otherSlot === slot) return { ...i, equipped: false, equippedSlot: null };
    // Two-handed in mainhand → clear offhand
    if (slot === 'mainHand' && otherSlot === 'offHand' && targetIsTwoHanded) {
      return { ...i, equipped: false, equippedSlot: null };
    }
    // Shield equipped → clear two-handed mainhand
    if (slot === 'offHand' && otherSlot === 'mainHand' && targetIsShield && isTwoHanded(i)) {
      return { ...i, equipped: false, equippedSlot: null };
    }
    return i;
  });
}

export function equippedItem(inventory, slot) {
  return (inventory || []).find((i) => i.equipped && effectiveSlotOf(i) === slot) || null;
}

// AC = base + dex (capped) + shield bonus. Manual overrides aren't applied
// here — the caller decides whether to write the computed value through.
export function computeAc(playerRecord) {
  const inv = playerRecord?.inventory || [];
  const dex = abilityMod(playerRecord?.abilityScores?.dex);

  const armor  = equippedItem(inv, 'armor');
  const offCat = catalogFor(equippedItem(inv, 'offHand'));
  const shieldBonus = offCat?.armor?.type === 'shield' ? (offCat.armor.acBonus ?? 2) : 0;

  if (!armor) {
    return 10 + dex + shieldBonus;
  }
  const armorMeta = catalogFor(armor)?.armor;
  if (!armorMeta) return 10 + dex + shieldBonus;
  const base = armorMeta.baseAc ?? 10;

  if (armorMeta.type === 'light')  return base + dex + shieldBonus;
  if (armorMeta.type === 'medium') return base + Math.min(dex, armorMeta.dexCap ?? 2) + shieldBonus;
  if (armorMeta.type === 'heavy')  return base + shieldBonus;
  return base + shieldBonus;
}

// Returns a textual breakdown for the equip-tab AC display:
//   "Plate · 18 (+0 DEX, +2 shield) = 20"
export function describeAc(playerRecord) {
  const inv = playerRecord?.inventory || [];
  const dex = abilityMod(playerRecord?.abilityScores?.dex);
  const armor  = equippedItem(inv, 'armor');
  const offHand = equippedItem(inv, 'offHand');
  const offCat  = catalogFor(offHand);
  const shieldBonus = offCat?.armor?.type === 'shield' ? (offCat.armor.acBonus ?? 2) : 0;
  const armorMeta = catalogFor(armor)?.armor;

  const parts = [];
  let total = 10;
  if (armorMeta) {
    parts.push(`${armor.name} ${armorMeta.baseAc ?? 10}`);
    total = armorMeta.baseAc ?? 10;
    if (armorMeta.type === 'light')         { parts.push(`+${dex} DEX`);                                total += dex; }
    else if (armorMeta.type === 'medium')   { parts.push(`+${Math.min(dex, armorMeta.dexCap ?? 2)} DEX (cap ${armorMeta.dexCap ?? 2})`); total += Math.min(dex, armorMeta.dexCap ?? 2); }
    else if (armorMeta.type === 'heavy')    { /* no dex */ }
  } else {
    parts.push(`Unarmored 10`);
    parts.push(`+${dex} DEX`);
    total += dex;
  }
  if (shieldBonus > 0) {
    parts.push(`+${shieldBonus} shield`);
    total += shieldBonus;
  }
  return { total, breakdown: parts.join(' · ') };
}

// Weapon proficiency check. `weaponProficiencies` is a flat array of names
// pulled from the catalog (e.g., ['Longsword','Shortsword','simple','martial']).
// 'simple' / 'martial' sentinels match all weapons of that tier.
export function isProficientWith(weaponItem, weaponProficiencies) {
  if (!weaponItem) return false;
  const list = weaponProficiencies || [];
  if (list.includes(weaponItem.name)) return true;
  const cat = catalogFor(weaponItem);
  const tier = cat?.weapon?.tier;
  if (tier === 'simple' && list.includes('simple')) return true;
  if (tier === 'martial' && list.includes('martial')) return true;
  return false;
}

// Get the weapon's metadata (damage die, properties, default stat).
export function weaponMeta(weaponItem) {
  return catalogFor(weaponItem)?.weapon || null;
}

// Armor proficiency check. `armorProficiencies` is a flat array of armor
// type sentinels: 'light' | 'medium' | 'heavy' | 'shield'.
export function isProficientWithArmor(armorItem, armorProficiencies) {
  if (!armorItem) return false;
  const list = armorProficiencies || [];
  const meta = catalogFor(armorItem)?.armor;
  if (!meta) return false;
  return list.includes(meta.type);
}

// 5e-style equip penalties — soft, not hard blocks. Players can equip
// anything; the DM (and UI) sees the consequences clearly.
//
//   - Non-proficient weapon: no proficiency bonus on attack (already handled
//     by quickAttackPlan). Special class abilities tied to the weapon should
//     not apply — surfaced as a warning the DM can enforce.
//   - Non-proficient armor or shield: disadvantage on STR/DEX ability
//     checks, saves, and attack rolls; can't cast spells. AC is unchanged.
//
// Returns null if no penalty applies, else { kind, label, detail }.
export function equipPenalty(item, playerRecord) {
  if (!item || !item.equipped) return null;
  if (item.category === 'weapon') {
    if (!isProficientWith(item, playerRecord?.weaponProficiencies)) {
      return {
        kind: 'no-prof-weapon',
        label: 'Not proficient',
        detail: 'No proficiency bonus on attacks. Weapon-tied class abilities (Sneak Attack, Maneuvers, etc.) cannot be used.',
      };
    }
  }
  if (item.category === 'armor') {
    const meta = catalogFor(item)?.armor;
    if (meta?.type === 'shield') {
      if (!isProficientWithArmor(item, playerRecord?.armorProficiencies)) {
        return {
          kind: 'no-prof-shield',
          label: 'Shield untrained',
          detail: 'Disadvantage on STR/DEX checks, saves, and attack rolls. Cannot cast spells.',
        };
      }
    } else if (meta?.type === 'light' || meta?.type === 'medium' || meta?.type === 'heavy') {
      if (!isProficientWithArmor(item, playerRecord?.armorProficiencies)) {
        return {
          kind: 'no-prof-armor',
          label: 'Armor untrained',
          detail: 'Disadvantage on STR/DEX checks, saves, and attack rolls. Cannot cast spells.',
        };
      }
    }
  }
  return null;
}

// Kept for backward compat with the failed hard-block path. Always returns ok.
export function canEquip() { return { ok: true }; }

// Pick the stat to use for an attack: weapon.stat by default; finesse weapons
// use whichever of STR/DEX is higher.
export function pickAttackStat(weaponItem, abilityScores) {
  const meta = weaponMeta(weaponItem);
  if (!meta) return 'str';
  if (meta.properties?.includes('finesse')) {
    const str = abilityScores?.str ?? 10;
    const dex = abilityScores?.dex ?? 10;
    return dex > str ? 'dex' : 'str';
  }
  return meta.stat || 'str';
}

// Build a {to-hit, damage} pair the caller will roll via lib/dice.js.
//   { toHit: { dice: [{sides:20, count:1}], modifier: stat+prof },
//     damage:{ dice: [{sides: N, count: M}],          modifier: stat } }
// Damage die strings like '2d6' are parsed; '1d8' or 'd8' both work.
export function quickAttackPlan({ weaponItem, playerRecord }) {
  const meta = weaponMeta(weaponItem);
  if (!meta) return null;
  const ability   = playerRecord?.abilityScores || {};
  const attackStat = pickAttackStat(weaponItem, ability);
  const statMod   = abilityMod(ability[attackStat]);
  const proficient = isProficientWith(weaponItem, playerRecord?.weaponProficiencies);
  const profMod   = proficient ? profBonusForLevel(playerRecord?.level || 1) : 0;
  // Magic weapons (e.g. the Tower relics) carry a flat bonus that applies to
  // both to-hit and damage — the 5e "+1 weapon".
  const magicBonus = meta.magicBonus || 0;

  // Damage die parsing: 'd8' → 1d8; 'd10' → 1d10; '2d6' → 2d6
  const dieStr = meta.damageDie || 'd4';
  const m = dieStr.match(/^(\d*)d(\d+)$/);
  const count = m ? (parseInt(m[1], 10) || 1) : 1;
  const sides = m ? parseInt(m[2], 10) : 4;

  return {
    statName: attackStat,
    statMod,
    profMod,
    proficient,
    magicBonus,
    toHit: {
      dice: [{ sides: 20, count: 1 }],
      modifier: statMod + profMod + magicBonus,
    },
    damage: {
      dice: [{ sides, count }],
      modifier: statMod + magicBonus,
    },
  };
}
